import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Checkbox, IconButton, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { FieldArray, useField, useFormikContext } from 'formik';
import React, { useState } from 'react';

import aggregateChannel from './aggregateChannel';
import {
  resolveChannelCellSx,
  resolveColumnHeaderSx,
  resolveNameCellSx,
  resolveTreeRowSx,
  TREE_PX,
} from './alarmTreeLayout';

// Channel name shown beside a control on phones, where the header row that
// names the columns does not render.  Hidden from the desktop table, whose
// header states each name once.
function ChannelCellLabel({ channel }) {
  return (
    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ display: { xs: 'flex', md: 'none' }, minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary">
        {channel.label}
      </Typography>
      {channel.tooltip ? (
        <Tooltip title={channel.tooltip} arrow placement="top">
          <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
        </Tooltip>
      ) : null}
    </Stack>
  );
}

// Bare formik-bound switch.  The visible channel name comes from the
// header row on a desktop and from `ChannelCellLabel` on a phone; the
// switch itself carries only its aria-label.
function AlarmChannelSwitch({ name, ariaLabel, disabled }) {
  const [field, , helpers] = useField(name);
  return (
    <Switch
      checked={Boolean(field.value)}
      disabled={disabled}
      onChange={(_, checked) => helpers.setValue(checked)}
      inputProps={{ 'aria-label': ariaLabel }}
    />
  );
}

function ColumnHeaders({ channels }) {
  return (
    <Box
      sx={{
        ...resolveColumnHeaderSx(channels.length),
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'action.hover',
      }}
    >
      <Box />
      {channels.map(ch => (
        <Stack
          key={ch.key}
          direction="row"
          spacing={0.25}
          alignItems="center"
          justifyContent="center"
          sx={{ minWidth: 0 }}
        >
          <Typography
            variant="caption"
            sx={{
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'text.secondary',
              fontWeight: 600,
            }}
          >
            {ch.label}
          </Typography>
          {ch.tooltip ? (
            <Tooltip title={ch.tooltip} arrow placement="top">
              <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          ) : null}
        </Stack>
      ))}
    </Box>
  );
}

function GroupHeader({ parent, indices, channels, isExpanded, onToggleExpanded, fieldName, disabled }) {
  const { values, setFieldValue } = useFormikContext();
  const alarms = values[fieldName] || [];
  const fanOut = (channelKey, value) => {
    if (disabled) return;
    indices.forEach(idx => setFieldValue(`${fieldName}[${idx}].${channelKey}`, value));
  };
  return (
    <Box
      sx={{
        ...resolveTreeRowSx(channels.length),
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'action.hover',
      }}
    >
      <Box sx={{ ...resolveNameCellSx(), display: 'flex', alignItems: 'center' }}>
        <IconButton
          size="small"
          onClick={onToggleExpanded}
          aria-label={isExpanded ? 'collapse' : 'expand'}
          sx={{ mr: 1, flexShrink: 0 }}
        >
          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {parent?.name ?? `#${parent?.id}`}
          </Typography>
          {parent?.description ? (
            <Typography variant="caption" color="text.secondary">
              {parent.description}
            </Typography>
          ) : null}
        </Stack>
      </Box>
      {channels.map(ch => {
        const { checked, indeterminate } = aggregateChannel(alarms, indices, ch.key);
        return (
          <Box key={ch.key} sx={resolveChannelCellSx()}>
            <ChannelCellLabel channel={ch} />
            <Checkbox
              checked={checked}
              indeterminate={indeterminate}
              disabled={disabled}
              onChange={(_, next) => fanOut(ch.key, next)}
              inputProps={{ 'aria-label': `${parent?.name ?? 'group'} ${ch.label}` }}
            />
          </Box>
        );
      })}
    </Box>
  );
}

function Row({ index, row, channels, indented, fieldName, disabled, rowTitle, rowSubtitle, rowChips }) {
  const title = rowTitle ? rowTitle(row) : (row.name ?? `#${row.id ?? index}`);
  const subtitle = rowSubtitle ? rowSubtitle(row) : null;
  const chips = rowChips ? rowChips(row) : null;
  return (
    <Box
      sx={{
        ...resolveTreeRowSx(channels.length),
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Stack spacing={0.25} sx={resolveNameCellSx({ indented })}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          {chips}
        </Stack>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        ) : null}
      </Stack>
      {channels.map(ch => (
        <Box key={ch.key} sx={resolveChannelCellSx({ indented })}>
          <ChannelCellLabel channel={ch} />
          <AlarmChannelSwitch
            name={`${fieldName}[${index}].${ch.key}`}
            ariaLabel={`${title} ${ch.label}`}
            disabled={disabled || ch.disabled}
          />
        </Box>
      ))}
    </Box>
  );
}

/**
 * Tree-rendering FieldArray for alarms / alarm subscriptions.
 *
 * Used by both the User settings (per-user notification subscriptions)
 * and Site settings (per-site enable/notify/ignore toggles) pages.
 * Same shape — flat array of rows in Formik state, parent→children
 * tree on screen with tri-state aggregate checkboxes that fan out
 * via setFieldValue on the underlying indices.
 *
 * Caller pre-computes `groups` / `orphans` via
 * `groupAlarmsByParent(alarms, getParent, opts)` because the parent
 * resolution differs slightly per surface (user-settings reads
 * `category.parent` from a side-fetched category map; site-settings
 * reads `row.parent_id` directly off the serializer payload).
 */
export default function AlarmTree({
  alarms,
  groups,
  orphans,
  channels,
  fieldName = 'alarms',
  disabled = false,
  emptyText,
  rowTitle,
  rowSubtitle,
  rowChips,
  rowKey,
}) {
  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpanded = parentId =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(parentId)) next.delete(parentId);
      else next.add(parentId);
      return next;
    });

  if (alarms.length === 0) {
    // Padded to match a row — the tree renders into a flush container, so
    // unpadded text sits against its border.
    return (
      <Typography variant="body2" color="text.secondary" sx={{ px: TREE_PX, py: 1.5 }}>
        {emptyText ?? 'No alarms.'}
      </Typography>
    );
  }

  const keyFor = (row, idx) => (rowKey ? rowKey(row, idx) : (row.id ?? row.alarm_id ?? row.alarm_category_id ?? idx));

  return (
    <FieldArray name={fieldName}>
      {() => (
        <Box>
          <ColumnHeaders channels={channels} />
          {groups.map(group => {
            const parentId = group.parent.id;
            const isExpanded = expanded.has(parentId);
            return (
              <React.Fragment key={parentId}>
                <GroupHeader
                  parent={group.parent}
                  indices={group.indices}
                  channels={channels}
                  isExpanded={isExpanded}
                  onToggleExpanded={() => toggleExpanded(parentId)}
                  fieldName={fieldName}
                  disabled={disabled}
                />
                {isExpanded &&
                  group.indices.map(idx => (
                    <Row
                      key={keyFor(alarms[idx], idx)}
                      index={idx}
                      row={alarms[idx]}
                      channels={channels}
                      indented
                      fieldName={fieldName}
                      disabled={disabled}
                      rowTitle={rowTitle}
                      rowSubtitle={rowSubtitle}
                      rowChips={rowChips}
                    />
                  ))}
              </React.Fragment>
            );
          })}
          {orphans.map(idx => (
            <Row
              key={keyFor(alarms[idx], idx)}
              index={idx}
              row={alarms[idx]}
              channels={channels}
              indented={false}
              fieldName={fieldName}
              disabled={disabled}
              rowTitle={rowTitle}
              rowSubtitle={rowSubtitle}
              rowChips={rowChips}
            />
          ))}
        </Box>
      )}
    </FieldArray>
  );
}
